import React, { useState } from "react"
import PropTypes from "prop-types"
import BioModal from "./bioModal"
import "./clinicTeam.scss"

// Cards cycle through five brand colors. Five never divides evenly into the
// 4, 3 or 2 columns the grid wraps to, so each row starts one color later and
// no color sits directly under itself (which read like a reporting line).
const TONES = ["pink", "blue", "yellow", "green", "teal"]

export const Chevron = () => (
  <svg
    aria-hidden="true"
    className="ClinicTeam__chevron"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      d="m9 6 6 6-6 6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
)

// "Carlsbad, CA" -> "Carlsbad", so the heading reads the way the design does.
const cityOf = name => (name || "").split(",")[0].trim()

const EMAIL = /[\w.+-]+@[\w.-]+\.\w+/
const PHONE = /\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}[^,]*/

/**
 * Contact details come from the Email and Phone fields on Staff Member.
 *
 * Before those fields existed, editors typed them into the role instead, e.g.
 * "Site Director, bsiegan@corticacare.com, (619) 377-6451". Those entries are
 * still read, and the role is cleaned up for display, so nothing breaks while
 * they are moved across. Retire the fallback once no entry does that.
 */
const parseContact = doctor => {
  const raw = doctor.role || ""
  const email = (raw.match(EMAIL) || [])[0]
  const phone = (raw.match(PHONE) || [])[0]

  const role = raw
    .split(",")
    .map(part => part.trim())
    .filter(part => part && !EMAIL.test(part) && !PHONE.test(part))
    .join(", ")

  return {
    ...doctor,
    email: doctor.email || email,
    phone: doctor.phone || phone,
    role: role || raw,
  }
}

// Phones show this many people when collapseOnMobile is set, with a button
// for the rest (used by the About page's leadership team).
const MOBILE_COUNT = 4

const ClinicTeam = ({
  clinic,
  collapseOnMobile = false,
  doctors,
  id,
  intro,
  moreLabel = "See full team",
  title,
}) => {
  const [openIndex, setOpenIndex] = useState(null)
  const [showAll, setShowAll] = useState(false)

  if (!doctors?.length) return null

  const city = cityOf(clinic?.name)
  const heading = city ? `The ${city} Autism Therapy Team` : title
  const people = doctors.map(parseContact)
  const open = openIndex === null ? null : people[openIndex]
  const collapsible = collapseOnMobile && people.length > MOBILE_COUNT

  return (
    <section className="ClinicTeam" id={id}>
      <div className="container-v2">
        <h2 className="ClinicTeam__title">{heading}</h2>
        {intro && <p className="ClinicTeam__intro">{intro}</p>}

        <ul
          className={`ClinicTeam__grid${
            collapsible && !showAll ? " ClinicTeam__grid--collapsed" : ""
          }`}
        >
          {people.map((doctor, index) => {
            // Only offer the dialog when there is something to read.
            const hasBio = Boolean(doctor.details?.json)
            const tone = TONES[index % TONES.length]
            const extra = collapsible && index >= MOBILE_COUNT

            return (
              <li
                className={`ClinicTeam__card ClinicTeam__card--${tone}${
                  extra ? " ClinicTeam__card--extra" : ""
                }`}
                key={doctor.slug || doctor.name}
              >
                {doctor.picture?.fluid?.src && (
                  <img
                    alt={doctor.name}
                    className="ClinicTeam__photo"
                    src={doctor.picture.fluid.src}
                  />
                )}

                <p className="ClinicTeam__name">{doctor.name}</p>
                {doctor.role && (
                  <p className="ClinicTeam__role">{doctor.role}</p>
                )}

                {hasBio && (
                  <button
                    aria-haspopup="dialog"
                    className="ClinicTeam__more"
                    onClick={() => setOpenIndex(index)}
                    type="button"
                  >
                    See More
                    <Chevron />
                  </button>
                )}
              </li>
            )
          })}
        </ul>

        {collapsible && (
          <div className="ClinicTeam__toggle-wrap">
            <button
              aria-expanded={showAll}
              className="ClinicTeam__toggle"
              onClick={() => setShowAll(!showAll)}
              type="button"
            >
              {showAll ? "Show fewer" : moreLabel}
              <span aria-hidden="true">{showAll ? "\u2212" : "+"}</span>
            </button>
          </div>
        )}
      </div>

      {open && (
        <BioModal
          doctor={open}
          onClose={() => setOpenIndex(null)}
          tone={TONES[openIndex % TONES.length]}
        />
      )}
    </section>
  )
}

ClinicTeam.propTypes = {
  clinic: PropTypes.shape({ name: PropTypes.string }),
  collapseOnMobile: PropTypes.bool,
  doctors: PropTypes.arrayOf(
    PropTypes.shape({
      details: PropTypes.shape({ json: PropTypes.object }),
      name: PropTypes.string,
      picture: PropTypes.shape({
        fluid: PropTypes.shape({ src: PropTypes.string }),
      }),
      role: PropTypes.string,
      slug: PropTypes.string,
    })
  ),
  id: PropTypes.string,
  intro: PropTypes.string,
  moreLabel: PropTypes.string,
  title: PropTypes.string,
}

export default ClinicTeam
